import {useMemo, useRef} from 'react'
import {RenderState, type PreviewData} from './types'
import {useIFrameMessaging} from './use-iframe-messaging'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'

import sandboxStyles from './SandboxView.module.css'
import {ErrorMessage} from './ErrorMessage'

function sandboxUrl(viewscreenUrl: string) {
  return new URL('view/sandbox?url=client://sandbox', viewscreenUrl).toString()
}

export interface SandboxProps extends PreviewData {
  viewscreenUrl: string
}

export function SandboxView({
  content,
  inlineScripts,
  inlineStyles,
  headScripts,
  bodyScripts,
  styles,
  viewscreenUrl,
}: SandboxProps) {
  const sandboxEnabled = isFeatureEnabled('viewscreen_sandbox')
  const url = sandboxUrl(viewscreenUrl)
  const origin = new URL(url, ssrSafeLocation.origin).origin

  const iFrameRef = useRef<HTMLIFrameElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const previewData = useMemo<PreviewData>(
    () => ({
      content,
      headScripts,
      bodyScripts,
      inlineScripts,
      inlineStyles,
      styles,
    }),
    [bodyScripts, content, headScripts, inlineScripts, inlineStyles, styles],
  )

  const {renderState, errorMessage} = useIFrameMessaging(iFrameRef, containerRef, previewData, origin)

  if (!sandboxEnabled) return null

  sendEvent('sandbox_view.activate', {target: 'SANDBOX_INITIALIZED', enabled: sandboxEnabled})

  // Collect the URL origins for headScripts, bodyScript, styles and send them as events to telemetry
  const origins = Array.from(
    new Set(
      [...(headScripts || []), ...(bodyScripts || []), ...(styles || [])]
        .map(script => {
          try {
            return new URL(script, origin).origin
          } catch {
            return null
          }
        })
        .filter(Boolean),
    ),
  )

  for (const resourceOrigin of origins) {
    sendEvent('sandbox_view.activate', {target: 'SANDBOX_RESOURCE_ORIGIN', origin: resourceOrigin})
  }

  return (
    <div ref={containerRef} className={sandboxStyles.container}>
      {renderState === RenderState.ERROR ? (
        <ErrorMessage error={errorMessage} />
      ) : (
        <iframe
          ref={iFrameRef}
          className={sandboxStyles.iframe}
          // eslint-disable-next-line @eslint-react/dom/no-unsafe-iframe-sandbox
          sandbox="allow-scripts allow-same-origin"
          title="Preview page for the user provided code"
          src={url}
        />
      )}
    </div>
  )
}
